/**
 * TokenManager - Centralized Authentication Token Management
 *
 * Provides centralized token storage, retrieval, and callback management
 * for the BiteWise healthcare provider platform. Supports configurable
 * storage backends with graceful error handling.
 *
 * Features:
 * - Token storage, retrieval, and clearing
 * - Token change callback system
 * - Configurable storage (default localStorage)
 * - Graceful error handling for storage operations
 * - TypeScript-first implementation
 */

import { AUTH_STORAGE } from '@/constants/storage';

/**
 * Callback interface for token change notifications
 */
export interface TokenChangeCallback {
  (token: string | null): void;
}

/**
 * TokenManager class for centralized authentication token management
 */
export class TokenManager {
  private token: string | null = null;
  private refreshPromise: Promise<string> | null = null;
  private callbacks: TokenChangeCallback[] = [];
  private storage: Storage;

  /**
   * Initialize TokenManager with optional storage backend
   * @param storage - Storage backend (defaults to localStorage)
   */
  constructor(storage: Storage = localStorage) {
    this.storage = storage;
    this.loadPersistedToken();
  }

  /**
   * Set authentication token and notify callbacks
   * @param token - Authentication token to store
   */
  setToken(token: string): void {
    this.token = token;
    this.persistToken(token);
    this.notifyCallbacks(token);
  }

  /**
   * Get current authentication token
   * @returns Current token or null if not set
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Clear authentication token and notify callbacks
   */
  clearToken(): void {
    this.token = null;
    this.clearPersistedToken();
    this.notifyCallbacks(null);
  }

  /**
   * Register callback for token changes
   * @param callback - Function to call when token changes
   * @returns Unsubscribe function to remove the callback
   */
  onTokenChange(callback: TokenChangeCallback): () => void {
    this.callbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Load persisted token from storage on initialization
   * @private
   */
  private loadPersistedToken(): void {
    try {
      const persistedToken = this.storage.getItem(AUTH_STORAGE.TOKEN);
      if (persistedToken) {
        this.token = persistedToken;
      }
    } catch (error) {
      console.warn('Failed to load persisted token from storage:', error);
      // Continue without persisted token
    }
  }

  /**
   * Persist token to storage
   * @private
   * @param token - Token to persist
   */
  private persistToken(token: string): void {
    try {
      this.storage.setItem(AUTH_STORAGE.TOKEN, token);
    } catch (error) {
      console.warn('Failed to persist token to storage:', error);
      // Continue without persistence
    }
  }

  /**
   * Clear persisted token from storage
   * @private
   */
  private clearPersistedToken(): void {
    try {
      this.storage.removeItem(AUTH_STORAGE.TOKEN);
    } catch (error) {
      console.warn('Failed to clear persisted token from storage:', error);
      // Continue without clearing persistence
    }
  }

  /**
   * Notify all registered callbacks of token changes
   * @private
   * @param token - New token value (or null if cleared)
   */
  private notifyCallbacks(token: string | null): void {
    this.callbacks.forEach(callback => {
      try {
        callback(token);
      } catch (error) {
        console.warn('Token change callback failed:', error);
        // Continue notifying other callbacks
      }
    });
  }
}

/**
 * Default TokenManager instance using localStorage
 */
export const tokenManager = new TokenManager();
